import { memo, useState } from 'react';
import { AlertTriangle, Copy, Check, Clipboard } from 'lucide-react';
import { useTranslation } from '@/hooks/useTranslation';

export interface GenerationErrorPanelProps {
  /** Human-readable error message */
  error: string;
  /** Machine-readable error code (e.g. FOOOCUS_NOT_RUNNING) */
  errorCode?: string;
  /** Actionable setup instructions surfaced by the backend */
  setupInstructions?: string;
  /** Job / generation identifier for diagnostics */
  generationId?: string;
  /** In-memory backend logs captured during the run */
  logs?: string[];
  /** Optional stage label shown alongside the error (e.g. 'video_generation') */
  stage?: string;
  className?: string;
}

function buildReport(args: {
  error: string;
  errorCode?: string;
  setupInstructions?: string;
  generationId?: string;
  logs?: string[];
  stage?: string;
}): string {
  const lines: string[] = [];
  lines.push('=== Generation Error Report ===');
  lines.push(`Timestamp: ${new Date().toISOString()}`);
  if (args.generationId) lines.push(`Generation ID: ${args.generationId}`);
  if (args.stage) lines.push(`Stage: ${args.stage}`);
  if (args.errorCode) lines.push(`Error code: ${args.errorCode}`);
  lines.push('');
  lines.push('Error:');
  lines.push(args.error);
  if (args.setupInstructions) {
    lines.push('');
    lines.push('Setup instructions:');
    lines.push(args.setupInstructions);
  }
  const logsArr = Array.isArray(args.logs) ? args.logs : [];
  if (logsArr.length > 0) {
    lines.push('');
    lines.push(`Logs (${logsArr.length} lines):`);
    lines.push(...logsArr);
  }
  return lines.join('\n');
}

export const GenerationErrorPanel = memo(function GenerationErrorPanel({
  error,
  errorCode,
  setupInstructions,
  generationId,
  logs,
  stage,
  className = '',
}: GenerationErrorPanelProps) {
  const { t } = useTranslation();
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    const report = buildReport({ error, errorCode, setupInstructions, generationId, logs, stage });
    try {
      await navigator.clipboard.writeText(report);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('[GenerationErrorPanel] Failed to copy report:', err);
    }
  };

  return (
    <div
      className={`mt-4 rounded-lg border border-red-500/30 bg-red-950/30 p-4 ${className}`}
      role="alert"
      data-testid="generation-error-panel"
    >
      <div className="flex items-start gap-3">
        <AlertTriangle className="h-5 w-5 flex-shrink-0 text-red-400 mt-0.5" aria-hidden="true" />
        <div className="flex-1 min-w-0">
          <h3 className="text-sm font-semibold text-red-200">
            {t('generationError.title', 'Generation failed')}
          </h3>
          <p className="mt-1 text-sm text-red-100/90 break-words">{error}</p>

          {errorCode && (
            <p className="mt-2 font-mono text-xs text-red-300/80">
              {t('generationError.code', 'Code')}: {errorCode}
            </p>
          )}

          {setupInstructions && (
            <div className="mt-3 rounded border border-amber-500/30 bg-amber-950/20 p-3">
              <p className="text-xs font-semibold text-amber-200 mb-1">
                {t('generationError.howToFix', 'How to fix')}
              </p>
              <p className="text-xs text-amber-100/90 whitespace-pre-wrap">{setupInstructions}</p>
            </div>
          )}

          <div className="mt-3 flex flex-wrap items-center gap-2">
            <button
              type="button"
              onClick={handleCopy}
              className="inline-flex items-center gap-2 rounded-md border border-white/10 bg-black/30 px-3 py-2 text-xs font-medium text-white/90 hover:bg-black/50 transition-colors min-h-[36px]"
              aria-label={t('generationError.copyReport', 'Copy error report')}
            >
              {copied ? (
                <>
                  <Check className="h-3.5 w-3.5 text-emerald-400" />
                  {t('generationError.copied', 'Copied')}
                </>
              ) : (
                <>
                  <Copy className="h-3.5 w-3.5" />
                  {t('generationError.copyReport', 'Copy error report')}
                </>
              )}
            </button>

            {generationId && (
              <span className="inline-flex items-center gap-1.5 text-xs text-white/50 font-mono">
                <Clipboard className="h-3 w-3" aria-hidden="true" />
                {generationId}
              </span>
            )}
          </div>
        </div>
      </div>
    </div>
  );
});
